import {
  checkbox as checkboxPrompt,
  confirm as confirmPrompt,
  input as inputPrompt,
  password as passwordPrompt,
  search as searchPrompt,
  select as selectPrompt,
} from "@inquirer/prompts";
import pc from "picocolors";

export type ProviderAuthMethod = "none" | "api-key" | "oauth" | "device-code";
export type CredentialSelection =
  | { source: "none" }
  | { source: "encrypted"; secret?: string; keepExisting?: boolean }
  | { source: "env"; name: string }
  | { source: "file"; path: string };

export type OnboardingSelection = {
  provider: string;
  authMethod: ProviderAuthMethod;
  credential: CredentialSelection;
  baseUrl?: string;
  model: string;
  channels: string[];
  installDaemon: boolean;
};

export type ModelDiscoveryRequest = Pick<OnboardingSelection, "provider" | "authMethod" | "credential" | "baseUrl">;
export type DiscoveredModel = { id: string; displayName?: string };

type Choice<Value extends string> = {
  value: Value;
  name: string;
  description?: string;
  checked?: boolean;
};

export type OnboardingPrompts = {
  select<Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>, defaultValue?: Value): Promise<Value>;
  search<Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>, defaultValue?: Value): Promise<Value>;
  checkbox<Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>): Promise<Value[]>;
  input(message: string, defaultValue?: string, validate?: (value: string) => boolean | string): Promise<string>;
  password(message: string, validate?: (value: string) => boolean | string): Promise<string>;
  confirm(message: string, defaultValue: boolean): Promise<boolean>;
  note(message: string): void;
};

type ProviderDescriptor = {
  id: string;
  name: string;
  description: string;
  env: string;
  local?: boolean;
  fallbackModels?: string[];
};

const PROVIDERS: ProviderDescriptor[] = [
  { id: "openai", name: "OpenAI", description: "API key or ChatGPT/Codex OAuth", env: "OPENAI_API_KEY", fallbackModels: ["gpt-4.1"] },
  { id: "anthropic", name: "Anthropic", description: "Claude models", env: "ANTHROPIC_API_KEY", fallbackModels: ["claude-sonnet-4-5"] },
  { id: "openrouter", name: "OpenRouter", description: "Multi-provider model routing", env: "OPENROUTER_API_KEY", fallbackModels: ["openai/gpt-4.1"] },
  { id: "ollama", name: "Ollama", description: "Local models on your computer", env: "OLLAMA_API_KEY", local: true, fallbackModels: ["qwen3:8b", "llama3.2"] },
  { id: "cerebras", name: "Cerebras", description: "Fast OpenAI-compatible inference", env: "CEREBRAS_API_KEY" },
  { id: "deepinfra", name: "DeepInfra", description: "Open-source model hosting", env: "DEEPINFRA_API_KEY" },
  { id: "deepseek", name: "DeepSeek", description: "DeepSeek models", env: "DEEPSEEK_API_KEY" },
  { id: "featherless", name: "Featherless", description: "Serverless open-source models", env: "FEATHERLESS_API_KEY" },
  { id: "fireworks", name: "Fireworks AI", description: "Fast generative AI inference", env: "FIREWORKS_API_KEY" },
  { id: "groq", name: "Groq", description: "Low-latency inference", env: "GROQ_API_KEY" },
  { id: "huggingface", name: "Hugging Face", description: "Inference Providers router", env: "HF_TOKEN" },
  { id: "lmstudio", name: "LM Studio", description: "Local OpenAI-compatible server", env: "LMSTUDIO_API_KEY", local: true },
  { id: "mistral", name: "Mistral AI", description: "Mistral models", env: "MISTRAL_API_KEY" },
  { id: "nvidia", name: "NVIDIA NIM", description: "NVIDIA-hosted model APIs", env: "NVIDIA_API_KEY" },
  { id: "together", name: "Together AI", description: "Open-source model platform", env: "TOGETHER_API_KEY" },
  { id: "venice", name: "Venice", description: "Private generative AI APIs", env: "VENICE_API_KEY" },
  { id: "xai", name: "xAI", description: "Grok models", env: "XAI_API_KEY" },
  { id: "vllm", name: "vLLM", description: "Local OpenAI-compatible server", env: "VLLM_API_KEY", local: true },
];

export const PROVIDER_ENV = Object.freeze(Object.fromEntries(PROVIDERS.map((provider) => [provider.id, provider.env])) as Record<string, string>);
export const CHANNEL_ENV = Object.freeze({ telegram: "TELEGRAM_BOT_TOKEN", discord: "DISCORD_BOT_TOKEN" } as Record<string, string>);

function terminalPrompts(write: (message: string) => void): OnboardingPrompts {
  return {
    select: async <Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>, defaultValue?: Value) => selectPrompt({ message, choices, ...(defaultValue ? { default: defaultValue } : {}), pageSize: 12 } as never) as Promise<Value>,
    search: async <Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>, defaultValue?: Value) => searchPrompt({
      message,
      ...(defaultValue ? { default: defaultValue } : {}),
      pageSize: 12,
      source: async (term: string | undefined) => {
        const query = term?.trim().toLowerCase() ?? "";
        if (!query) return choices.slice(0, 100);
        return choices.filter((choice) => `${choice.name} ${choice.value} ${choice.description ?? ""}`.toLowerCase().includes(query)).slice(0, 100);
      },
    } as never) as Promise<Value>,
    checkbox: async <Value extends string>(message: string, choices: ReadonlyArray<Choice<Value>>) => checkboxPrompt({ message, choices, pageSize: 8, loop: false } as never) as Promise<Value[]>,
    input: async (message, defaultValue, validate) => inputPrompt({ message, ...(defaultValue ? { default: defaultValue } : {}), ...(validate ? { validate } : {}) }),
    password: async (message, validate) => passwordPrompt({ message, mask: "•", ...(validate ? { validate } : {}) }),
    confirm: async (message, defaultValue) => confirmPrompt({ message, default: defaultValue }),
    note: (message) => write(`${pc.dim("│")} ${message.replaceAll("\n", `\n${pc.dim("│")} `)}\n`),
  };
}

function providerFor(id: string): ProviderDescriptor {
  return PROVIDERS.find((provider) => provider.id === id) ?? PROVIDERS[0]!;
}

function modelIdForProvider(model: string | undefined, provider: string): string | undefined {
  if (!model) return undefined;
  return model.startsWith(`${provider}/`) ? model.slice(provider.length + 1) : undefined;
}

function validModelId(value: string): boolean | string {
  const model = value.trim();
  if (!model) return "Enter a model ID";
  if (model.length > 512) return "Model IDs must be at most 512 characters";
  if (/\s/u.test(model)) return "Model IDs cannot contain whitespace";
  return true;
}

function validUrl(value: string): boolean | string {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "Use an HTTP or HTTPS URL";
    if (url.username || url.password) return "Do not put credentials in the URL";
    return true;
  } catch {
    return "Enter a valid URL";
  }
}

async function chooseCredential(
  prompts: OnboardingPrompts,
  provider: ProviderDescriptor,
  options: { existingCredential: boolean; existingEnv?: string; existingFile?: string; env: NodeJS.ProcessEnv },
): Promise<CredentialSelection> {
  const detectedEnv = options.existingEnv ?? provider.env;
  const choices: Array<Choice<"saved" | "encrypted" | "env" | "file">> = [];
  if (options.existingCredential) choices.push({ value: "saved", name: "Keep saved encrypted key", description: "Use the credential already in Nori's encrypted vault" });
  choices.push(
    { value: "encrypted", name: "Enter and encrypt an API key", description: "Store it in Nori's local AES-256-GCM vault" },
    { value: "env", name: `Environment variable${options.env[detectedEnv] ? " (detected)" : ""}`, description: `Read the key from ${detectedEnv}` },
    { value: "file", name: "Private secret file", description: "Read the key from a file at runtime" },
  );
  const source = await prompts.select("How should Nori access the API key?", choices, options.existingCredential ? "saved" : options.env[detectedEnv] ? "env" : "encrypted");
  if (source === "saved") return { source: "encrypted", keepExisting: true };
  if (source === "encrypted") {
    const secret = await prompts.password(`${provider.name} API key`, (value) => value.trim() ? true : "Enter an API key");
    return { source: "encrypted", secret: secret.trim() };
  }
  if (source === "env") {
    const name = await prompts.input("Environment variable name", detectedEnv, (value) => /^[A-Z_][A-Z0-9_]*$/u.test(value.trim()) || "Use an uppercase environment variable name");
    return { source: "env", name: name.trim() };
  }
  const secretPath = await prompts.input("Secret file path", options.existingFile, (value) => value.trim() ? true : "Enter a file path");
  return { source: "file", path: secretPath.trim() };
}

async function chooseModel(
  prompts: OnboardingPrompts,
  provider: ProviderDescriptor,
  request: ModelDiscoveryRequest,
  existingModel: string | undefined,
  discoverModels: (request: ModelDiscoveryRequest) => Promise<DiscoveredModel[]>,
): Promise<string> {
  let discovered: DiscoveredModel[] = [];
  try {
    prompts.note(`Discovering ${provider.name} models…`);
    discovered = await discoverModels(request);
    if (discovered.length) prompts.note(`Found ${discovered.length} available model${discovered.length === 1 ? "" : "s"}. Type to filter the list.`);
  } catch (error) {
    const message = (error as Error).message.replaceAll(/\s+/gu, " ").slice(0, 240);
    prompts.note(`${pc.yellow("Model discovery unavailable:")} ${message}`);
  }

  const current = modelIdForProvider(existingModel, provider.id);
  const unique = new Map<string, DiscoveredModel>();
  if (current) unique.set(current, { id: current, displayName: "Current model" });
  for (const model of discovered) if (model.id) unique.set(model.id, model);
  for (const id of provider.fallbackModels ?? []) if (!unique.has(id)) unique.set(id, { id });

  const custom = "__nori_custom_model__";
  const choices: Array<Choice<string>> = [...unique.values()].map((model) => ({
    value: model.id,
    name: model.displayName && model.displayName !== model.id ? `${model.displayName} (${model.id})` : model.id,
    ...(model.id === current ? { description: "currently configured" } : {}),
  }));
  choices.push({ value: custom, name: "Enter a model ID…", description: "Use a model not shown in discovery" });
  const selected = await prompts.search("Choose the default model", choices, current && unique.has(current) ? current : choices[0]!.value);
  const rawModel = selected === custom
    ? await prompts.input("Model ID", current ?? provider.fallbackModels?.[0], validModelId)
    : selected;
  const modelId = rawModel.trim().startsWith(`${provider.id}/`) ? rawModel.trim().slice(provider.id.length + 1) : rawModel.trim();
  const valid = validModelId(modelId);
  if (valid !== true) throw new Error(String(valid));
  return `${provider.id}/${modelId}`;
}

export type InteractiveOnboardingOptions = {
  preferredProvider?: string;
  preferredAuthMethod?: Exclude<ProviderAuthMethod, "none">;
  preferredModel?: string;
  selectedChannels?: string[];
  installDaemon?: boolean;
  existingProviders?: Record<string, { baseUrl?: string; hasEncryptedCredential?: boolean; env?: string; file?: string }>;
  env?: NodeJS.ProcessEnv;
  discoverModels(request: ModelDiscoveryRequest): Promise<DiscoveredModel[]>;
  write?: (message: string) => void;
};

export async function runInteractiveOnboarding(
  options: InteractiveOnboardingOptions,
  prompts: OnboardingPrompts = terminalPrompts(options.write ?? ((message) => process.stdout.write(message))),
): Promise<OnboardingSelection | undefined> {
  const env = options.env ?? process.env;
  try {
    prompts.note(`${pc.cyan(pc.bold("Nori setup"))} ${pc.cyan("🐶")}\nUse ↑/↓ to move, type to search models, Space to toggle, and Enter to continue.`);
    while (true) {
      const providerDefault = PROVIDERS.some((provider) => provider.id === options.preferredProvider) ? options.preferredProvider : "openai";
      const providerId = await prompts.select(
        "Choose a model provider",
        PROVIDERS.map((provider) => ({ value: provider.id, name: provider.name, description: provider.description })),
        providerDefault,
      );
      const provider = providerFor(providerId);
      const existingProvider = options.existingProviders?.[provider.id];

      let authMethod: ProviderAuthMethod = "none";
      let credential: CredentialSelection = { source: "none" };
      if (provider.local) {
        prompts.note(`${provider.name} uses a local endpoint, so an API key is optional.`);
      } else if (provider.id === "openai") {
        authMethod = await prompts.select(
          "Authenticate with OpenAI",
          [
            { value: "oauth", name: "Browser OAuth (recommended)", description: "Sign in with ChatGPT/Codex in your browser" },
            { value: "device-code", name: "Device code", description: "Best for headless and remote machines" },
            { value: "api-key", name: "OpenAI API key", description: "Use a Platform API key" },
          ],
          options.preferredAuthMethod ?? "oauth",
        );
        if (authMethod === "api-key") credential = await chooseCredential(prompts, provider, {
          existingCredential: Boolean(existingProvider?.hasEncryptedCredential),
          ...(existingProvider?.env ? { existingEnv: existingProvider.env } : {}),
          ...(existingProvider?.file ? { existingFile: existingProvider.file } : {}),
          env,
        });
      } else {
        authMethod = "api-key";
        credential = await chooseCredential(prompts, provider, {
          existingCredential: Boolean(existingProvider?.hasEncryptedCredential),
          ...(existingProvider?.env ? { existingEnv: existingProvider.env } : {}),
          ...(existingProvider?.file ? { existingFile: existingProvider.file } : {}),
          env,
        });
      }

      const useCustomEndpoint = await prompts.confirm("Use a custom API endpoint?", Boolean(existingProvider?.baseUrl));
      const baseUrl = useCustomEndpoint
        ? (await prompts.input("API base URL", existingProvider?.baseUrl, validUrl)).replace(/\/$/u, "")
        : undefined;
      const discovery = { provider: provider.id, authMethod, credential, ...(baseUrl ? { baseUrl } : {}) } satisfies ModelDiscoveryRequest;
      const model = await chooseModel(prompts, provider, discovery, options.preferredModel, options.discoverModels);

      const initialChannels = new Set(options.selectedChannels ?? []);
      const channels = await prompts.checkbox(
        "Enable messaging channels (optional)",
        [
          { value: "telegram", name: "Telegram", description: "Bot API via TELEGRAM_BOT_TOKEN", checked: initialChannels.has("telegram") },
          { value: "whatsapp", name: "WhatsApp", description: "Link with a QR code after setup", checked: initialChannels.has("whatsapp") },
          { value: "discord", name: "Discord", description: "Bot Gateway via DISCORD_BOT_TOKEN", checked: initialChannels.has("discord") },
        ],
      );
      const installDaemon = await prompts.confirm("Install the always-on Nori gateway service?", Boolean(options.installDaemon));

      let authLabel = authMethod === "device-code" ? "OpenAI device code" : authMethod === "oauth" ? "OpenAI browser OAuth" : "Local endpoint";
      if (authMethod === "api-key") {
        if (credential.source === "encrypted") authLabel = "Encrypted API key";
        else if (credential.source === "env") authLabel = `Environment: ${credential.name}`;
        else if (credential.source === "file") authLabel = `Secret file: ${credential.path}`;
      }
      prompts.note([
        pc.bold("Review"),
        `Provider: ${provider.name}`,
        `Authentication: ${authLabel}`,
        `Model: ${model}`,
        `Channels: ${channels.length ? channels.join(", ") : "CLI only"}`,
        `Gateway service: ${installDaemon ? "install" : "not installed"}`,
      ].join("\n"));
      const action = await prompts.select(
        "Save this configuration?",
        [
          { value: "save", name: "Save and finish", description: "Write private Nori configuration and credentials" },
          { value: "restart", name: "Start over", description: "Change the selections" },
          { value: "cancel", name: "Cancel", description: "Leave the current configuration unchanged" },
        ],
        "save",
      );
      if (action === "restart") continue;
      if (action === "cancel") return undefined;
      return { provider: provider.id, authMethod, credential, ...(baseUrl ? { baseUrl } : {}), model, channels, installDaemon };
    }
  } catch (error) {
    if ((error as Error).name === "ExitPromptError") return undefined;
    throw error;
  }
}
